import * as moment from 'moment-timezone';
import { Subject } from 'rxjs';

import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { trigger, state, style, animate, transition, query, animateChild } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import {
  Challenge,
  ChallengeService,
  Team,
  TeamUser,
  TeamMembershipStatus,
} from 'app/shared/services/challenge.service';
import { UserService } from 'app/shared/services/user.service';
import { Router } from '@angular/router';
import { User } from 'app/shared/models/User';
import { MatSnackBar } from '@angular/material';

enum ChallengeStatus {
  RUNNING = 'running',
  FUTURE = 'future',
}

@Component({
  selector: 'app-box',
  templateUrl: './box.component.html',
  styleUrls: ['./box.component.scss'],
  animations: [
    trigger('openDelete', [
      transition(':enter', [
        style({
          opacity: '0',
          height: '0',
          width: '0',
          'border-radius': '50%',
          overflow: 'hidden',
          top: 'unset',
          left: 'unset',
        }),
        animate('.3s ease-out', style({ opacity: '1', height: '*', width: '*', 'border-radius': '0' })),
      ]),
      transition(':leave', [style({ opacity: '1' }), animate('.3s ease-out', style({ opacity: '0' }))]),
    ]),
  ],
})
export class BoxComponent implements OnInit, OnDestroy {
  @ViewChild('myVideo') myVideo: ElementRef;
  @Input() boxType: string;
  @Input() type: string;
  @Input() id: number;
  @Input() showBorder: boolean;

  @Input() preview: string;
  @Input() title: string;
  @Input() description: string;
  @Input() categories: any;
  @Input() num: number;
  @Input() selected: boolean;
  @Input() videoUrl: string;

  @Input() hideButtons: boolean = false;

  // User Interaction by clicking on element
  @Output() onSelect: EventEmitter<any> = new EventEmitter();
  @Output() onEdit: EventEmitter<any> = new EventEmitter();
  @Output() onRemove: EventEmitter<any> = new EventEmitter();

  public up: string = 'up';
  public mp: string = 'mp';
  public ex: string = 'ex';
  public new: string = 'new';
  public play: string = 'play';
  public add: string = 'add';
  public remove: string = 'remove';

  public showDelete: boolean = false;

  public currentUser: User;

  /** Challenge specific attributes */
  challenge: Challenge;

  // Team of user (if user has a team)
  public team: Team;

  public teamUser: TeamUser;

  // Whether challenge is in past, present or future
  public challengeStatus: ChallengeStatus;

  // Depending on challenge status:
  // future -> Days until challenge starts
  // present -> Days until challenge ends
  // past -> Days since challenge has ended
  public displayedTimeframe: any;

  public showLeaveTeamDialog: boolean = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  // Reference to ChallengeStatus to be able to use it in template
  public ChallengeStatus: typeof ChallengeStatus = ChallengeStatus;
  // Reference to TeamMembershipStatus to be able to use it in template
  public TeamMembershipStatus: typeof TeamMembershipStatus = TeamMembershipStatus;

  public videoDuration: number;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private userService: UserService,
    private challengeService: ChallengeService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Subscribe to current user to be able to find out his team status
   */
  public ngOnInit(): void {
    this.userService.currentUser.takeUntil(this.ngUnsubscribe).subscribe((user) => {
      if (!user) {
        return;
      }
      this.currentUser = user;
    });

    if (this.boxType === 'challenge') {
      this.challengeService.displayedChallenge.takeUntil(this.ngUnsubscribe).subscribe((challenge) => {
        this.challenge = challenge;
        this.setChallengeTimeFrame();
      });

      this.challengeService.team.takeUntil(this.ngUnsubscribe).subscribe((team) => {
        this.team = team;
      });

      this.challengeService.teamUser.takeUntil(this.ngUnsubscribe).subscribe((teamUser) => {
        this.teamUser = teamUser;
      });
    }

    const videoElement: HTMLVideoElement = this.myVideo.nativeElement;

    videoElement.addEventListener('loadedmetadata', () => {
      this.videoDuration = videoElement.duration;
    });

    videoElement.load();
  }

  clickOnActionbutton(): void {
    this.onSelect.emit();
  }
  editBox(): void {
    this.onEdit.emit();
  }
  removeAskBox(event): void {
    this.showDelete = true;
  }

  closeAskRemove() {
    this.showDelete = false;
  }

  removeBox(e: MouseEvent) {
    e.stopPropagation();
    this.onRemove.emit();
  }

  /**
   * Display or hide leave team dialog (only for team members who are not the founder)
   */
  public showLeaveTeam(show: boolean) {
    this.showLeaveTeamDialog = show;
  }

  public confirmLeaveTeam() {
    this.challengeService.removeUserFromTeam(this.team.challengeID, this.team._id, this.currentUser._id).catch(() => {
      this.translate.get('CHALLENGE.error.general').subscribe((text) => {
        this.snackBar.open(text, null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
        this.challengeService.reloadTeams();
      });
    });
  }

  public declineInvitation() {
    this.challengeService.declineInvitation(this.team.challengeID, this.team._id, this.currentUser.email).catch(() => {
      this.translate.get('CHALLENGE.error.general').subscribe((text) => {
        this.snackBar.open(text, null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
        this.challengeService.reloadTeams();
      });
    });
  }

  public acceptInvitation() {
    this.challengeService
      .acceptInvitation(this.team.challengeID, this.team._id, this.currentUser._id)
      .catch((error) => {
        const errorObject = error.body.error.error;

        if (errorObject.startsWith('maximum_challenge_members')) {
          this.translate.get('CHALLENGE.error.maximumChallengeMemberReached').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
        } else {
          this.translate.get('CHALLENGE.error.general').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
            this.challengeService.reloadTeams();
          });
        }
      });
  }

  public cancelApplication() {
    this.challengeService.cancelApplication(this.team.challengeID, this.team._id, this.currentUser._id).catch(() => {
      this.translate.get('CHALLENGE.error.general').subscribe((text) => {
        this.snackBar.open(text, null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
        this.challengeService.reloadTeams();
      });
    });
  }

  /**
   * Decides whether challenge is in past,present in future
   * and which number for remaining or passed days should be shown
   */
  private setChallengeTimeFrame() {
    if (!this.challenge) {
      return;
    }

    if (moment(this.challenge.start).isAfter(moment())) {
      this.challengeStatus = ChallengeStatus.FUTURE;
      this.displayedTimeframe = this.getDisplayedTimeframe(moment(this.challenge.start));
    } else if (moment(this.challenge.end).isAfter(moment())) {
      this.challengeStatus = ChallengeStatus.RUNNING;
      this.displayedTimeframe = this.getDisplayedTimeframe(moment(this.challenge.end));
    } else {
      this.challengeStatus = null;
      this.displayedTimeframe = null;
    }
  }

  /**
   * Decides based on language and time difference between date and now, which time is displayed
   */
  private getDisplayedTimeframe(date: moment.Moment) {
    const language = this.currentUser.language;

    // For calculating date difference in localized user time, compare dates from start of day
    const startOfToday = moment().tz(this.currentUser.timeZone).startOf('day');
    const startOfReferencedDay = moment(date).tz(this.currentUser.timeZone).startOf('day');
    const dateDifference = Math.abs(startOfReferencedDay.diff(startOfToday, 'days'));

    if (dateDifference >= 2) {
      return language === 'de' ? 'In ' + dateDifference + ' Tagen' : 'in ' + dateDifference + ' days';
    } else {
      return date.tz(this.currentUser.timeZone).calendar(
        null,
        language === 'de'
          ? { sameDay: '[Heute um] HH:mm [Uhr]', nextDay: '[Morgen um] HH:mm [Uhr]' }
          : {
              sameDay: '[Today at] LT',
              nextDay: '[Tomorrow at] LT',
            }
      );
    }
  }

  public navigateSelectTeam(): void {
    this.router.navigate(['challenge', this.challenge._id, 'team']).then();
  }

  public openChallengeTutorial(): void {
    this.router.navigate(['challengeTutorial']).then();
  }

  public navigateTeamDetails(): void {
    this.router.navigate(['stats']).then();
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/theme/components/box/box.component.ts